import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Users, MapPin, Clock, Calendar, Mail, Phone, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { WorkshopPriceDisplay } from '../components/PriceDisplay';
import { workshopAgenda, workshopLearnings } from '../data/mockData';
import { useCasesAPI } from '../services/api';
import { useLanguage } from '../context/LanguageContext';

const WorkshopPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [useCase, setUseCase] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    jobTitle: '',
    company: '',
    preferredDate: '',
    participants: '',
    interests: '',
  });
  const [submitted, setSubmitted] = useState(false);

  // Fetch use case from API
  useEffect(() => {
    const fetchUseCase = async () => {
      try {
        const data = await useCasesAPI.getById(id);
        setUseCase(data);
      } catch (error) {
        console.error('Failed to fetch use case:', error);
        setUseCase(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUseCase();
  }, [id]);

  const handleSubmit = (e) => {
    e.preventDefault();
    // In production, this would send to backend
    setSubmitted(true);
  };

  // Helper to get translated text
  const getTranslatedText = (uc, field) => {
    if (!uc) return '';
    const nlField = `${field}_nl`;
    if (language === 'nl' && uc[nlField]) {
      return uc[nlField];
    }
    return uc[field] || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto" />
            <p className="text-gray-600 mt-4">{language === 'nl' ? 'Laden...' : 'Loading...'}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!useCase) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h1 className="text-2xl font-bold text-gray-900">
              {language === 'nl' ? 'Use case niet gevonden' : 'Use case not found'}
            </h1>
            <Button onClick={() => navigate('/use-cases')} className="mt-4">
              {language === 'nl' ? 'Terug naar Use Cases' : 'Back to Use Cases'}
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const useCaseTitle = getTranslatedText(useCase, 'title');

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <main className="py-12">
          <div className="max-w-2xl mx-auto px-4 text-center">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {language === 'nl' ? 'Aanvraag verzonden!' : 'Request Submitted!'}
              </h1>
              <p className="text-gray-600 mb-6">
                {language === 'nl' 
                  ? 'We hebben je workshop aanvraag ontvangen. Ons team neemt binnen 24 uur contact met je op om de beschikbaarheid te bevestigen.'
                  : "We've received your workshop request. Our team will contact you within 24 hours to confirm availability."}
              </p>
              <Button onClick={() => navigate('/use-cases')} className="bg-blue-500 hover:bg-blue-600">
                {language === 'nl' ? 'Bekijk meer Use Cases' : 'Browse More Use Cases'}
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-50">
      <Header />
      
      <main className="py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-500 rounded-xl mb-4">
              <span className="text-white text-2xl">💡</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {language === 'nl' ? 'AI Workshop voor HR (4u)' : 'AI Workshop for HR (4h)'}
            </h1>
            <p className="text-gray-600 mb-2">
              {useCaseTitle}
            </p>
            <p className="text-gray-500 text-sm">
              {language === 'nl' ? '1-2 use cases – van idee naar werkend prototype' : '1-2 use cases – from idea to working prototype'}
            </p>
            <div className="mt-4">
              <WorkshopPriceDisplay language={language} variant="default" showCountdown={true} />
            </div>
          </div>

          {/* What you'll learn */}
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-2xl p-6 mb-6 text-white">
            <h2 className="text-xl font-bold mb-4">
              {language === 'nl' ? 'Wat je leert in deze workshop' : "What you'll learn in this workshop"}
            </h2>
            <div className="space-y-2">
              {workshopLearnings.map((learning, index) => (
                <div key={learning.en || learning.nl || `learn-${index}`} className="flex items-start gap-2">
                  <CheckCircle className="h-5 w-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span>{language === 'nl' ? learning.nl : learning.en}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Agenda */}
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              Agenda ({language === 'nl' ? '4 uur' : '4 hours'})
            </h2>
            <div className="space-y-4">
              {workshopAgenda.map((item, index) => (
                <div key={item.time || `slot-${index}`} className="flex gap-4">
                  <div className="text-blue-500 font-mono text-sm font-medium w-28 flex-shrink-0">
                    {item.time}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">
                      {language === 'nl' ? (item.titleNL || item.title) : item.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {language === 'nl' ? (item.descriptionNL || item.description) : item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 text-center">
              <Users className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">{language === 'nl' ? 'Team Workshop' : 'Team Workshop'}</h3>
              <p className="text-sm text-gray-500">{language === 'nl' ? 'Ideaal voor 5-15 HR professionals' : 'Ideal for 5-15 HR professionals'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <MapPin className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">{language === 'nl' ? 'Locatie' : 'Location'}</h3>
              <p className="text-sm text-gray-500">{language === 'nl' ? 'Op locatie of online' : 'On-site at your location or online'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 text-center">
              <Clock className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900">{language === 'nl' ? 'Duur' : 'Duration'}</h3>
              <p className="text-sm text-gray-500">{language === 'nl' ? '4 uur intensief, hands-on' : '4 hours intensive, hands-on'}</p>
            </div>
          </div>

          {/* Booking Form */}
          <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
              {language === 'nl' ? 'Interesse? Boek je workshop' : 'Interested? Book your workshop'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Naam' : 'Name'} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Telefoonnummer' : 'Phone number'} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="tel"
                    required
                    placeholder="+32 123 45 67 89"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Functie' : 'Job Title'} <span className="text-red-500">*</span>
                  </label>
                  <Input
                    required
                    placeholder="HR Director, CHRO..."
                    value={formData.jobTitle}
                    onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'nl' ? 'Bedrijfsnaam' : 'Company name'}
                </label>
                <Input
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Voorkeursdatum' : 'Preferred date'}
                  </label>
                  <Input
                    type="date"
                    value={formData.preferredDate}
                    onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'nl' ? 'Aantal deelnemers' : 'Number of participants'}
                  </label>
                  <Input
                    placeholder="5-15"
                    value={formData.participants}
                    onChange={(e) => setFormData({ ...formData, participants: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {language === 'nl' ? 'Specifieke interesses / use cases' : 'Specific interests / use cases'}
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  rows={3}
                  placeholder={language === 'nl' ? 'Bijv. We willen focussen op recruitment AI...' : 'E.g. We want to focus on recruitment AI...'}
                  value={formData.interests}
                  onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                />
              </div>

              <Button type="submit" className="w-full bg-blue-500 hover:bg-blue-600 py-6">
                <Mail className="h-4 w-4 mr-2" />
                {language === 'nl' ? 'Beschikbaarheid aanvragen' : 'Request availability'}
              </Button>
            </form>
          </div>

          {/* Contact */}
          <div className="text-center text-gray-600">
            <Phone className="h-4 w-4 inline mr-2" />
            {language === 'nl' ? 'Vragen? Neem contact op: ' : 'Questions? Contact us: '}
            <a href="mailto:dominique@humanistix.be" className="text-blue-500 hover:underline">dominique@humanistix.be</a>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default WorkshopPage;
